import { safeWindow } from '../../utils/window.ts';
import { AppContext } from './app-context.tsx';

export type WindowAppContext<T extends AppContext> = { skAppContext: T };

export const getOrSetGlobalAppContext = <T extends AppContext>(fallback: () => T): T => {
  const window = safeWindow<WindowAppContext<T>>();

  if (!window) {
    throw new Error(
      'getOrSetGlobalAppContext was called in an environment without a window object'
    );
  } else {
    if (!window.skAppContext) {
      window.skAppContext = fallback();
    }
    return window.skAppContext;
  }
};

export const setGlobalAppContext = <T extends AppContext>(fallback: () => T): void => {
  const window = safeWindow<WindowAppContext<T>>();

  if (!window) {
    throw new Error('setGlobalAppContext was called in an environment without a window object');
  } else {
    window.skAppContext = fallback();
  }
};

export const updateGlobalAppContext = <T extends AppContext>(
  partial: Partial<AppContext>
): void => {
  const window = safeWindow<WindowAppContext<T>>();
  if (window?.skAppContext) {
    window.skAppContext = {
      ...window?.skAppContext,
      ...partial
    };
  } else {
    globalAppContext()?.logger.error(
      'updateGlobalAppContext was called without an existing global AppContext'
    );
  }
};

export const globalAppContext = <T extends AppContext>(): T | undefined => {
  const window = safeWindow<WindowAppContext<T>>();

  if (!window) {
    throw new Error('globalAppContext() was called in an environment without a window object');
  } else {
    // The unlikely case, that the appContext was not set, is just ignored here.
    return window.skAppContext;
  }
};
